import { HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreateSyncConfigurationRequest,
  FavoriteList,
  OutlookSyncType,
  SyncHealthStatus,
  SyncHistory,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
} from '../models';
import { OutlookSyncHttpBase } from './outlook-sync-http-base';

@Injectable()
export class UserSyncConfigurationService extends OutlookSyncHttpBase {
  getConfiguration(
    userId: string,
    workspaceId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    const params = new HttpParams()
      .set('userUid', userId)
      .set('workspaceId', workspaceId)
      .set('allowEmpty', 'true');
    return this.safeGet<UserSyncConfiguration>(this.syncConfigBasePath, {
      headers: this.buildHeaders({ ignoreInsights: true }),
      onError: (e) => console.error(e),
      params,
    });
  }

  // Same request as getConfiguration. Kept as a named method so the install precheck/race-check
  // reads intentionally; safeGet normalizes a 404 (no config) to undefined instead of throwing.
  getConfigurationForInstallPrecheck(
    userId: string,
    workspaceId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    return this.getConfiguration(userId, workspaceId);
  }

  updateConfiguration(
    settings: UpdateSyncConfiguration,
    id: string,
  ): Observable<boolean> {
    const encodedId = encodeURIComponent(id);
    return this.http
      .put<void>(
        `${this.syncConfigBasePath}/${encodedId}`,
        settings,
        {
          headers: this.buildHeaders({
            ignoreInsights:
              !!settings.maximizerAuth?.personalAccessToken ||
              !!settings.microsoftAuth?.refreshToken,
            extra: { 'Content-Type': 'application/json-patch+json' },
          }),
          observe: 'response',
        },
      )
      .pipe(
        this.mapStatusOk({
          allowed: [HttpStatusCode.Ok, HttpStatusCode.NoContent],
        }),
      );
  }

  getHistoryBySyncType(
    type: OutlookSyncType,
    id: string,
    pageSize = 5,
    offset = 0,
  ): Observable<SyncHistory[] | undefined> {
    const params = new HttpParams()
      .set('syncType', type)
      .set('pageSize', pageSize.toString())
      .set('offset', offset.toString());
    const encodedId = encodeURIComponent(id);
    return this.safeGet<SyncHistory[]>(
      `${this.syncConfigBasePath}/${encodedId}/sync-histories`,
      { params },
    );
  }

  getHealthStatus(id: string): Observable<SyncHealthStatus | undefined> {
    const encodedId = encodeURIComponent(id);
    return this.safeGet<SyncHealthStatus>(
      `${this.syncConfigBasePath}/${encodedId}/health-status`,
    );
  }

  getFavoriteLists(syncConfigurationId: string): Observable<FavoriteList[]> {
    const encodedId = encodeURIComponent(syncConfigurationId);
    return this.http.get<FavoriteList[]>(
      `${this.syncConfigBasePath}/${encodedId}/favorite-lists`,
      {
        headers: this.buildHeaders(),
      },
    );
  }

  createConfiguration(
    request: CreateSyncConfigurationRequest,
  ): Observable<string> {
    return this.http.post(this.syncConfigBasePath, request, {
      headers: this.buildHeaders({
        ignoreInsights:
          !!request.maximizerAuth?.personalAccessToken ||
          !!request.microsoftAuth?.refreshToken,
      }),
      responseType: 'text',
    });
  }

  getConfigurationById(
    syncConfigurationId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    const encodedId = encodeURIComponent(syncConfigurationId);
    return this.safeGet<UserSyncConfiguration>(
      `${this.syncConfigBasePath}/${encodedId}`,
      {
        headers: this.buildHeaders({ ignoreInsights: true }),
        onError: (e) =>
          console.error('Error retrieving sync configuration by id:', e),
      },
    );
  }
}
